import { Button, buttonVariants } from '@barfacil/barfacil-ui'
import { PropsWithChildren } from 'react'
import { Link } from 'react-router-dom'

import pageError from '@/assets/images/page-error-alternative.svg'
import { cn } from '@/lib/utils'

type FeedbackBlockProps = React.HTMLAttributes<HTMLElement>

type FeedbackBlockGraphicProps = React.HTMLAttributes<HTMLDivElement>

type FeedbackBlockTitleProps = Omit<
  React.HTMLAttributes<HTMLHeadingElement>,
  'children'
> & {
  title?: string
}

type FeedbackBlockDescriptionProps = Omit<
  React.HTMLAttributes<HTMLParagraphElement>,
  'children'
> & {
  description: string
}

type FeedbackBlockButtonsProps = PropsWithChildren & {
  onReload?: () => void
  reloadButton?: boolean
  dashboardButton?: boolean
}

const FeedbackBlock = ({
  className,
  children,
  ...props
}: FeedbackBlockProps) => {
  return (
    <section
      className={cn(
        'flex h-full animate-show-in items-center px-6 py-10 text-center',
        className,
      )}
      {...props}
    >
      <div className="flex w-full flex-col items-center justify-center">
        {children}
      </div>
    </section>
  )
}

const FeedbackBlockGraphic = ({
  className,
  children,
  ...props
}: FeedbackBlockGraphicProps) => {
  return (
    <div className={cn('mb-8', className)} {...props}>
      {children || (
        <img
          src={pageError}
          alt="Usuário encontrando erro na tela do computador com um sinal de alerta"
        />
      )}
    </div>
  )
}

const FeedbackBlockTitle = ({
  className,
  title = 'Ocorreu um erro na página',
  ...props
}: FeedbackBlockTitleProps) => {
  return (
    <h2
      className={cn(
        'mb-3 max-w-md text-center text-lg font-semibold md:text-xl',
        className,
      )}
      {...props}
    >
      {title}
    </h2>
  )
}

const FeedbackBlockDescription = ({
  className,
  description,
  ...props
}: FeedbackBlockDescriptionProps) => {
  return (
    <p className={cn('text-gray-50', className)} {...props}>
      {description}
    </p>
  )
}

const FeedbackBlockButtons = ({
  dashboardButton = true,
  reloadButton = true,
  children,
  onReload,
}: FeedbackBlockButtonsProps) => {
  function handleRefetch() {
    onReload ? onReload() : window.location.reload()
  }

  return (
    <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
      {children}

      {dashboardButton && (
        <Link
          to="/"
          className={cn(
            `${buttonVariants({ variant: 'ghost', size: 'lg' })} w-44 border px-0`,
          )}
        >
          Voltar para o Início
        </Link>
      )}

      {reloadButton && (
        <Button
          variant="secondary"
          onClick={handleRefetch}
          size="lg"
          className="w-44"
        >
          Recarregar
        </Button>
      )}
    </div>
  )
}

export {
  FeedbackBlock,
  FeedbackBlockGraphic,
  FeedbackBlockTitle,
  FeedbackBlockDescription,
  FeedbackBlockButtons,
}
